import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { catalogSongMessages } from './catalogSongMessages';
import { normalizeIsrc } from './normalizeIsrc';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song files; each part uses its own slice of it */
import styles from './SongFiles.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface IsrcFieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Shows the error also before the field lost the focus (the form was sent). */
  forceError?: boolean;
  /**
   * The classes of the form around the field, so the ISRC input has the height, the font and the
   * padding of the inputs next to it (the add-song wizard). Without them the song-files look applies.
   */
  classNames?: { field?: string; input?: string; hint?: string; error?: string };
}

/** True when the text is empty (no ISRC) or a good ISRC. */
export function isIsrcAcceptable(value: string): boolean {
  return value.trim() === '' || normalizeIsrc(value) !== null;
}

/**
 * The optional ISRC of an unreleased row (design g2). Dashes and spaces are ignored. The error
 * shows when the field loses the focus with a value that is not 12 good characters.
 */
const IsrcFieldComponent: React.FC<IsrcFieldProps> = ({
  value,
  onChange,
  disabled = false,
  forceError = false,
  classNames,
}) => {
  const intl = useIntl();
  const [isTouched, setIsTouched] = useState(false);
  const label = intl.formatMessage(catalogSongMessages.isrc);
  const showError = (isTouched || forceError) && !isIsrcAcceptable(value);

  const change = useCallback((event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value), [onChange]);
  const blur = useCallback(() => setIsTouched(true), []);

  return (
    <div className={classNames?.field ?? styles.field}>
      <span className={styles.label}>
        {label} <span className={styles.labelTag}>· {intl.formatMessage(catalogSongMessages.optional)}</span>
      </span>
      <input
        className={clsx(classNames?.input ?? styles.input, showError && styles.inputBad)}
        value={value}
        disabled={disabled}
        aria-label={label}
        aria-invalid={showError}
        placeholder="GB-ABC-26-00042"
        maxLength={20}
        onChange={change}
        onBlur={blur}
      />
      {showError ? (
        <span className={classNames?.error ?? styles.error} role="alert">
          {intl.formatMessage(catalogSongMessages.isrcError)}
        </span>
      ) : (
        <span className={classNames?.hint ?? styles.small}>
          {withBrandWord(intl.formatMessage(catalogSongMessages.isrcHelp))}
        </span>
      )}
    </div>
  );
};

export const IsrcField = TypedMemo(IsrcFieldComponent);
